import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "../styles/team.css";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE || "https://primerise.space";

const TEAM_COMPLETION_TARGET = 5;

const Team = () => {
  const [user, setUser] = useState(null);
  const [teamStats, setTeamStats] = useState({
    totalTeam: 0,
    level1: 0,
    level2: 0,
    level3: 0,
    members: [],
    salaryProgram: {
      activeDirectMembers: 0,
      qualified: false,
      level: 0,
      weeklySalary: 0,
      label: "Not Qualified",
      weekKey: "",
      alreadyClaimedThisWeek: false,
      nextTarget: 5,
    },
  });
  const [loading, setLoading] = useState(true);
  const [claimLoading, setClaimLoading] = useState(false);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("primeriseToken") ||
      localStorage.getItem("naxoraToken") ||
      localStorage.getItem("authToken") ||
      ""
    );
  };

  const getStoredUser = () => {
    try {
      return (
        JSON.parse(localStorage.getItem("naxoraCurrentUser")) ||
        JSON.parse(localStorage.getItem("currentUser")) ||
        JSON.parse(localStorage.getItem("user")) ||
        null
      );
    } catch {
      return null;
    }
  };

  const saveUserEverywhere = (nextUser) => {
    localStorage.setItem("user", JSON.stringify(nextUser));
    localStorage.setItem("currentUser", JSON.stringify(nextUser));
    localStorage.setItem("naxoraCurrentUser", JSON.stringify(nextUser));
  };

  const getDefaultTeamStats = () => {
    return {
      totalTeam: 0,
      level1: 0,
      level2: 0,
      level3: 0,
      members: [],
      salaryProgram: {
        activeDirectMembers: 0,
        qualified: false,
        level: 0,
        weeklySalary: 0,
        label: "Not Qualified",
        weekKey: "",
        alreadyClaimedThisWeek: false,
        nextTarget: 5,
      },
    };
  };

  const fetchProfile = async () => {
    const token = getToken();
    const storedUser = getStoredUser();

    if (!token) {
      setUser(storedUser);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok || !data.user) {
        setUser(storedUser);
        return;
      }

      const backendUser = data.user || {};
      const mergedUser = {
        ...(storedUser || {}),
        ...backendUser,
      };

      saveUserEverywhere(mergedUser);
      setUser(mergedUser);
    } catch {
      setUser(storedUser);
    }
  };

  const fetchTeamStats = async () => {
    const token = getToken();

    if (!token) {
      setTeamStats(getDefaultTeamStats());
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/team`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        setTeamStats(getDefaultTeamStats());
        return;
      }

      const members = Array.isArray(data.members) ? data.members : [];
      const salaryProgram = data.salaryProgram || {};

      setTeamStats({
        totalTeam: Number(data.totalTeam || 0),
        level1: Number(data.level1 || 0),
        level2: Number(data.level2 || 0),
        level3: Number(data.level3 || 0),
        members,
        salaryProgram: {
          activeDirectMembers: Number(salaryProgram.activeDirectMembers || 0),
          qualified: salaryProgram.qualified === true,
          level: Number(salaryProgram.level || 0),
          weeklySalary: Number(salaryProgram.weeklySalary || 0),
          label: String(salaryProgram.label || "Not Qualified"),
          weekKey: String(salaryProgram.weekKey || ""),
          alreadyClaimedThisWeek:
            salaryProgram.alreadyClaimedThisWeek === true,
          nextTarget:
            salaryProgram.nextTarget === null
              ? null
              : Number(salaryProgram.nextTarget || 5),
        },
      });

      if (data.referralCode) {
        setUser((prev) => {
          const nextUser = {
            ...(prev || {}),
            myReferralCode: data.referralCode,
            referralCode: data.referralCode,
          };
          saveUserEverywhere(nextUser);
          return nextUser;
        });
      }
    } catch {
      setTeamStats(getDefaultTeamStats());
    }
  };

  useEffect(() => {
    const loadTeamPage = async () => {
      setLoading(true);
      await fetchProfile();
      await fetchTeamStats();
      setLoading(false);
    };

    loadTeamPage();
  }, []);

  const referralCode = useMemo(() => {
    if (!user) return "";
    return String(user.myReferralCode || user.referralCode || "").trim();
  }, [user]);

  const referralLink = useMemo(() => {
    if (!referralCode) return "";
    return `${window.location.origin}/register?ref=${encodeURIComponent(
      referralCode
    )}`;
  }, [referralCode]);

  const completion = useMemo(() => {
    const done = Math.min(
      Number(teamStats.totalTeam || 0),
      TEAM_COMPLETION_TARGET
    );
    return `${done}/${TEAM_COMPLETION_TARGET}`;
  }, [teamStats]);

  const activeMembers = useMemo(() => {
    return teamStats.members.filter(
      (member) => String(member.status || "").toLowerCase() === "active"
    ).length;
  }, [teamStats.members]);

  const inactiveMembers = useMemo(() => {
    return teamStats.members.filter(
      (member) => String(member.status || "").toLowerCase() !== "active"
    ).length;
  }, [teamStats.members]);

  const salaryProgram = useMemo(() => {
    return teamStats.salaryProgram || {
      activeDirectMembers: 0,
      qualified: false,
      level: 0,
      weeklySalary: 0,
      label: "Not Qualified",
      weekKey: "",
      alreadyClaimedThisWeek: false,
      nextTarget: 5,
    };
  }, [teamStats.salaryProgram]);

  const salaryButtonDisabled =
    claimLoading ||
    salaryProgram.qualified !== true ||
    salaryProgram.alreadyClaimedThisWeek === true;

  const salaryButtonText = useMemo(() => {
    if (claimLoading) return "Processing...";
    if (salaryProgram.alreadyClaimedThisWeek) return "Already Claimed";
    if (salaryProgram.qualified) return "Claim Weekly Salary";
    return "Not Qualified";
  }, [claimLoading, salaryProgram]);

  const handleCopy = async (value, label) => {
    try {
      if (!value) {
        throw new Error(`${label} not available`);
      }

      await navigator.clipboard.writeText(value);

      Swal.fire({
        icon: "success",
        title: "Copied",
        text: `${label} copied successfully`,
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || `Unable to copy ${label.toLowerCase()}`,
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const handleClaimSalary = async () => {
    const token = getToken();

    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Login Required",
        text: "Please login again to claim weekly salary",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    if (salaryButtonDisabled) {
      return;
    }

    setClaimLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/team/salary/claim`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Unable to claim weekly salary");
      }

      if (data.balance !== undefined) {
        setUser((prev) => {
          const nextUser = {
            ...(prev || {}),
            balance: Number(data.balance || 0),
          };
          saveUserEverywhere(nextUser);
          return nextUser;
        });
      }

      if (data.salaryProgram) {
        setTeamStats((prev) => ({
          ...prev,
          salaryProgram: {
            activeDirectMembers: Number(
              data.salaryProgram.activeDirectMembers || 0
            ),
            qualified: data.salaryProgram.qualified === true,
            level: Number(data.salaryProgram.level || 0),
            weeklySalary: Number(data.salaryProgram.weeklySalary || 0),
            label: String(data.salaryProgram.label || "Not Qualified"),
            weekKey: String(data.salaryProgram.weekKey || ""),
            alreadyClaimedThisWeek:
              data.salaryProgram.alreadyClaimedThisWeek === true,
            nextTarget:
              data.salaryProgram.nextTarget === null
                ? null
                : Number(data.salaryProgram.nextTarget || 5),
          },
        }));
      }

      Swal.fire({
        icon: "success",
        title: "Salary Paid",
        text: data.message || "Weekly salary added successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      await fetchProfile();
      await fetchTeamStats();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to claim weekly salary",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setClaimLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="team-page">
        <div className="team-wrapper">
          <h1 className="team-title">Team</h1>
          <div className="team-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="team-page">
      <div className="team-wrapper">
        <h1 className="team-title">Team</h1>

        <div className="team-grid">
          <div className="team-card referral-card">
            <h2>Referral Access</h2>

            <div className="referral-box">
              <div className="referral-box-left">
                <span className="referral-label">Your Referral Code</span>
                <strong className="referral-value">
                  {referralCode || "Not Available"}
                </strong>
              </div>

              <button
                type="button"
                className="referral-copy-btn"
                onClick={() => handleCopy(referralCode, "Referral code")}
              >
                Copy
              </button>
            </div>

            <div className="referral-link-box">
              <span className="referral-label">Referral Link</span>
              <p className="referral-link-text">
                {referralLink || "Not Available"}
              </p>

              <button
                type="button"
                className="referral-link-btn"
                onClick={() => handleCopy(referralLink, "Referral link")}
              >
                Copy Link
              </button>
            </div>

            <div className="team-note-box">
              Share your referral code or referral link to grow your network.
            </div>
          </div>

          <div className="team-card stats-card">
            <h2>Team Statistics</h2>

            <div className="team-stats-grid">
              <div className="team-stat-item">
                <span>Total Team</span>
                <strong>{teamStats.totalTeam}</strong>
              </div>

              <div className="team-stat-item">
                <span>Level 1</span>
                <strong>{teamStats.level1}</strong>
              </div>

              <div className="team-stat-item">
                <span>Level 2</span>
                <strong>{teamStats.level2}</strong>
              </div>

              <div className="team-stat-item">
                <span>Level 3</span>
                <strong>{teamStats.level3}</strong>
              </div>
            </div>

            <div className="team-completion-box">
              <span>Team Completion</span>
              <strong>{completion}</strong>
            </div>
          </div>
        </div>

        <div className="team-card salary-card">
          <div className="salary-header">
            <div>
              <h2>Weekly Salary Program</h2>
              <p className="salary-subtitle">
                Direct members with balance above $50 are counted as active.
              </p>
            </div>

            <button
              type="button"
              className="salary-claim-btn"
              disabled={salaryButtonDisabled}
              onClick={handleClaimSalary}
            >
              {salaryButtonText}
            </button>
          </div>

          <div className="salary-stats-grid">
            <div className="salary-stat-item">
              <span>Active Direct Members</span>
              <strong>{salaryProgram.activeDirectMembers || 0}</strong>
            </div>

            <div className="salary-stat-item">
              <span>Qualified Level</span>
              <strong>{salaryProgram.label || "Not Qualified"}</strong>
            </div>

            <div className="salary-stat-item">
              <span>Weekly Salary</span>
              <strong>${Number(salaryProgram.weeklySalary || 0)}</strong>
            </div>

            <div className="salary-stat-item">
              <span>Status</span>
              <strong>
                {salaryProgram.alreadyClaimedThisWeek
                  ? "Paid This Week"
                  : salaryProgram.qualified
                  ? "Qualified"
                  : "Not Qualified"}
              </strong>
            </div>
          </div>

          <div className="salary-levels">
            <div className="salary-level-row">
              <span>5 Active Members</span>
              <strong>$18 Weekly</strong>
            </div>
            <div className="salary-level-row">
              <span>10 Active Members</span>
              <strong>$38 Weekly</strong>
            </div>
            <div className="salary-level-row">
              <span>20 Active Members</span>
              <strong>$85 Weekly</strong>
            </div>
            <div className="salary-level-row">
              <span>50 Active Members</span>
              <strong>$230 Weekly</strong>
            </div>
            <div className="salary-level-row">
              <span>100 Active Members</span>
              <strong>$500 Weekly</strong>
            </div>
          </div>
        </div>

        <div className="team-card team-members-card">
          <div className="team-members-header">
            <div>
              <h2>My Team Members</h2>
              <p className="team-members-subtitle">
                Usernames and current deposit activity status
              </p>
            </div>

            <div className="team-members-summary">
              <div className="team-summary-pill">
                <span>Active</span>
                <strong>{activeMembers}</strong>
              </div>
              <div className="team-summary-pill inactive">
                <span>Inactive</span>
                <strong>{inactiveMembers}</strong>
              </div>
            </div>
          </div>

          {!Array.isArray(teamStats.members) ||
          teamStats.members.length === 0 ? (
            <div className="team-empty-members">No team members found</div>
          ) : (
            <div className="team-members-list">
              {teamStats.members.map((member, index) => {
                const isActive =
                  String(member.status || "").toLowerCase() === "active";

                return (
                  <div className="team-member-row" key={member.id || index}>
                    <div className="team-member-left">
                      <span className="team-member-number">{index + 1}</span>

                      <div className="team-member-info">
                        <strong>{member.username || "User"}</strong>
                        <span>
                          {isActive
                            ? "At least one approved deposit"
                            : "No approved deposit yet"}
                        </span>
                      </div>
                    </div>

                    <div
                      className={`team-member-status ${
                        isActive ? "active" : "inactive"
                      }`}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Team;